// MortgageGraph.tsx

import React, { useMemo } from "react";

import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  LabelList,
} from "recharts";

import { renderCustomLabel, renderCustomLabel2 } from "./GraphLables";

// === Types ===

export interface MortgageDataItem {
  interestRate?: number;
  ownInterestRate?: number;
  name: string;
}

interface MortgageGraphProps {
  mortgageData: MortgageDataItem[];
  yAxisMargin?: number;
  height?: number;
  showXAxisLabels?: boolean;
  xAxisTitle?: string;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  labelFormatter?: (label: string) => string;
  valueFormatter?: (value: number, name: string) => string;
}

// === Constants ===

const DEFAULT_HEIGHT = 300;
const DEFAULT_Y_MARGIN = 0.1;
const DEFAULT_DOMAIN_RANGE: [number, number] = [0, 5];

const COLORS = {
  interestRate: "#1C2654",
  ownInterestRate: "#FF8D69",
  ownActiveDot: "#FF3A33",
};

// === Custom Tooltip ===

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
}) => {
  if (!active || !payload || payload.length === 0) return null;

  const formattedLabel = labelFormatter ? labelFormatter(label) : label;

  return (
    <div
      className="custom-tooltip"
      style={{
        backgroundColor: "white",
        border: "1px solid #ccc",
        borderRadius: "6px",
        padding: "10px",
        fontSize: "13px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
      role="tooltip"
    >
      <p style={{ margin: "0 0 6px", fontWeight: "bold" }}>{formattedLabel}</p>
      {payload.map((entry, index) => {
        const name = String(entry.name);
        const value = entry.value;
        if (typeof value !== "number") return null;

        const formattedValue =
          valueFormatter?.(value, name) ??
          (name.includes("Rate") ? `${value.toFixed(2)}%` : value.toString());

        return (
          <p
            key={`tooltip-item-${index}`}
            style={{
              margin: "2px 0",
              color: entry.color,
              fontWeight: name === "ownInterestRate" ? "bold" : "normal",
            }}
          >
            {`${name === "interestRate" ? "Rente" : name === "ownInterestRate" ? "Egen rente" : name}: ${formattedValue}`}
          </p>
        );
      })}
    </div>
  );
};

// === Label Renderer with Safety ===

const SafeLabelList = ({
  render,
  dataKey,
}: {
  dataKey: string;
  render: (props: { x?: number; y?: number; value?: number }, index: number) => React.ReactNode;
}): JSX.Element => {
  return (
    <LabelList
      dataKey={dataKey}
      content={(labelProps: any) => {
        const { index } = labelProps;
        if (index === undefined || typeof index !== 'number') return null;
        return <>{render(labelProps, index)}</>;
      }}
    />
  ) as JSX.Element;
};

// === Main Component ===

const MortgageGraph: React.FC<MortgageGraphProps> = ({
  mortgageData,
  yAxisMargin = DEFAULT_Y_MARGIN,
  height = DEFAULT_HEIGHT,
  showXAxisLabels = true,
  xAxisTitle,
}) => {
  const { lowestInterest, highestInterest } = useMemo(() => {
    const rates = mortgageData.flatMap((item) => [
      item.interestRate,
      item.ownInterestRate,
    ].filter((r): r is number => typeof r === "number"));

    if (rates.length === 0) {
      return { lowestInterest: DEFAULT_DOMAIN_RANGE[0], highestInterest: DEFAULT_DOMAIN_RANGE[1] };
    }

    return {
      lowestInterest: Math.min(...rates),
      highestInterest: Math.max(...rates),
    };
  }, [mortgageData]);

  // Safely compute domain with 2 decimal places
  const domain = useMemo(() => {
    const pad = (val: number) => {
      const padded = val < 0 ? val - yAxisMargin : val + yAxisMargin;
      return Number(padded.toFixed(2));
    };

    const min = pad(lowestInterest);
    const max = pad(highestInterest);

    // Ensure min < max
    return min >= max ? [min, min + 0.5] : [min, max];
  }, [lowestInterest, highestInterest, yAxisMargin]);

  const tickFormatter = (value: number): string => `${value.toFixed(1)}%`;

  return (
    <div role="img" aria-label="Sammenligning av rentetilbud for boliglån">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={mortgageData}
          margin={{ top: 30, right: 20, bottom: xAxisTitle ? 30 : 10, left: 10 }}
        >
          {/* X-Axis */}
          <XAxis
            dataKey="name"
            tickLine={false}
            axisLine={false}
            tick={false}
            padding={{ left: 20, right: 20 }}
            height={60}
            tickFormatter={() => ""}
            label={
              xAxisTitle
                ? {
                    value: xAxisTitle,
                    position: "insideBottomRight",
                    style: { fontSize: 13, fill: "#888" },
                  }
                : undefined
            }
          />

          {/* Y-Axis */}
          <YAxis
            domain={domain}
            tickFormatter={tickFormatter}
            tick={{ fontSize: 12, fill: "#666" }}
            axisLine={false}
            tickLine={false}
            width={50}
          />

          {/* Grid */}
          <CartesianGrid stroke="#e0e0e0" strokeDasharray="4 4" vertical={false} />

          {/* Standard Interest Rate Line */}
          <Line
            type="monotone"
            dataKey="interestRate"
            stroke={COLORS.interestRate}
            strokeWidth={2}
            dot={{ r: 4, fill: COLORS.interestRate, strokeWidth: 2 }}
            activeDot={{ r: 6 }}
            name="Rente"
          >
            <SafeLabelList
              dataKey="interestRate"
              render={(props: { x?: number; y?: number; value?: number }, index: number) => renderCustomLabel(props, index)}
            />
          </Line>

          {/* Own Interest Rate Line */}
          <Line
            type="monotone"
            dataKey="ownInterestRate"
            stroke={COLORS.ownInterestRate}
            strokeWidth={2.5}
            dot={{ r: 6, fill: COLORS.ownInterestRate, stroke: COLORS.ownInterestRate, strokeWidth: 2 }}
            activeDot={{ r: 7, fill: COLORS.ownActiveDot, stroke: COLORS.ownInterestRate }}
            name="Egen rente"
          >
            <SafeLabelList
              dataKey="ownInterestRate"
              render={(props: { x?: number; y?: number; value?: number }, index: number) => renderCustomLabel2(props, index)}
            />
          </Line>

          {/* Tooltip */}
          <Tooltip
            content={
              <CustomTooltip
                labelFormatter={(label) => `Tilbud: ${label}`}
                valueFormatter={(value, name) =>
                  name.includes("Rate") ? value.toFixed(2) : value.toString()
                }
              />
            }
            cursor={{ strokeDasharray: "3 3" }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MortgageGraph;
